import React, { useState } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { Link, useNavigate } from "react-router-dom";
import Spinner from "../spinner/Spinner";
import { useSupplyVendors } from "../../hooks/useInventory";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";

const SupplyVendorList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useSupplyVendors({ search, status, page, per_page: 15 });
  const vendors = data?.data?.vendors || [];
  const total = data?.data?.total || 0;
  const perPage = data?.data?.per_page || 15;
  const totalPages = Math.ceil(total / perPage);

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Supply Vendors</h1>
          <Link
            to="/head-office/inventory/supply-vendors/create"
            className="bg-orange1 text-white px-4 py-2 rounded hover:bg-orange-600 transition"
          >
            + New Vendor
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search vendors..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1 flex-1 min-w-[200px]"
          />
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {isLoading ? (
          <Spinner loading={isLoading} />
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {["Name", "Phone", "Email", "Contact Person", "Status", "Action"].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vendors.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                        No supply vendors found.{" "}
                        <Link to="/head-office/inventory/supply-vendors/create" className="text-orange1 underline">
                          Create one
                        </Link>
                      </td>
                    </tr>
                  ) : (
                    vendors.map((v) => (
                      <tr key={v.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{v.name}</div>
                          {v.products_supplied && (
                            <div className="text-xs text-gray-400 mt-1 truncate max-w-[200px]">{v.products_supplied}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{v.phone_number}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{v.email || "-"}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {v.contact_person_name || "-"}
                          {v.contact_person_phone && (
                            <div className="text-xs text-gray-400">{v.contact_person_phone}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span style={{
                            padding: "2px 10px",
                            borderRadius: "9999px",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            backgroundColor: v.status === "active" ? "#dcfce7" : "#fee2e2",
                            color: v.status === "active" ? "#166534" : "#991b1b",
                          }}>
                            {v.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          <div className="flex items-center gap-3">
                            <Link to={`/head-office/inventory/supply-vendors/show?id=${v.id}`} className="text-orange1 hover:text-orange-600" title="View">
                              <Eye width={18} height={18} />
                            </Link>
                            <Link to={`/head-office/inventory/supply-vendors/edit?id=${v.id}`} className="text-blue-600 hover:text-blue-800" title="Edit">
                              <PencilSquare width={18} height={18} />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">Total: {total}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-40 hover:bg-gray-50"
                  >
                    Prev
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-600">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-40 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayouts>
  );
};

export default SupplyVendorList;
